import React, { useState, useRef, useContext, useEffect } from 'react'
import { graphql } from 'gatsby'
import Button from '../components/Button'
import emailjs from 'emailjs-com'
import SplitText from '../components/SplitText'
import { ContextLoader } from '../context';
import { detectElementsOnScreen, killObservers } from '../helpers/detectElementsOnScreen'
import Seo from '../components/Seo'

const FORM_TEMPLATE_ID = 'contact_template'
const EMAIL_SERVICE_ID = process.env.GATSBY_EMAILJS_SERVICE_ID
const EMAIL_USER_ID = process.env.GATSBY_EMAILJS_USER_ID

const Contact = ({ data, location }) => {
	const pageData = data.prismicContact.data

	const [isLoading] = useContext(ContextLoader);
	useEffect(() => {
		if (isLoading === true) return
		const [observers, elements] = detectElementsOnScreen()
		return () => {
			killObservers(observers, elements)
		}
	}, [isLoading])

	const defaultUserInfos = {
		name: '',
		email: '',
		message: ''
	}
	const [userInfos, setUserInfos] = useState(defaultUserInfos)
	const [successMessage, setSuccessMessage] = useState(null)
	const [errorMessage, setErrorMessage] = useState(null)
	const [isFormLoading, setIsFormLoading] = useState(null)
	const formEl = useRef(null)

	const handleInputChange = (property, value, inputEl) => {
		const userInfosClone = {...userInfos}
		userInfosClone[property] = value
		setUserInfos(userInfosClone)
		inputEl.parentNode.classList.toggle('filled', value !== '')
	}

	const handleFormSubmit = e => {
		const isFormValid = formEl.current.reportValidity()

		if (!isFormValid) return

		setSuccessMessage(null)
		setErrorMessage(null)
		setIsFormLoading(true)

		emailjs.send(EMAIL_SERVICE_ID, FORM_TEMPLATE_ID, userInfos, EMAIL_USER_ID)
			.then(
				res => {
					setIsFormLoading(false)
					setSuccessMessage('Your message was sent. We will get back to you as soon as possible. Thank you.')
					setUserInfos(defaultUserInfos)
				},
				err => {
					setIsFormLoading(false)
					setErrorMessage('There was an error while submitting your email address. Please try again later or contact us by email to resolve the problem.')
				}
			)
	}

	const handleInputFocus = e => {
		e.target.parentNode.classList.add('focus')
	}
	const handleInputBlur = e => {
		e.target.parentNode.classList.remove('focus')
	}

  return (
    <main className="Contact">
			<Seo
				title="Contact"
				description={pageData.meta_description.text}
				url={location.href}
			/>
			<section className="Contact__FirstSection" data-menu-color="lWhite,mWhite,sWhite">
				<h1 className="Contact__Catchphrase h1 enterByLetters" data-is-on-screen>
					<SplitText splitBy="chars">
						{pageData.catchphrase.text}
					</SplitText>
				</h1>
				<form className="Contact__Form" ref={formEl} data-menu-color="lWhite,mGrey,sGrey">
					<div className="Contact__FormRow">
						<div className="Contact__FormInputContainer">
							<span className="Contact__FormInputLabel">Name</span>
							<input
								className="Contact__FormInput"
								type="text"
								required
								value={userInfos.name}
								onChange={e => handleInputChange('name', e.target.value, e.target)}
								onFocus={e => handleInputFocus(e)}
								onBlur={e => handleInputBlur(e)}
							/>
						</div>
						<div className="Contact__FormInputContainer">
							<span className="Contact__FormInputLabel">Email</span>
							<input
								className="Contact__FormInput"
								type="email"
								required
								value={userInfos.email}
								onChange={e => handleInputChange('email', e.target.value, e.target)}
								onFocus={e => handleInputFocus(e)}
								onBlur={e => handleInputBlur(e)}
							/>
						</div>
					</div>
					<div className="Contact__FormInputTextareaContainer">
						<span className="Contact__FormInputLabel">Message</span>
						<textarea
							className="Contact__FormInputTextarea"
							rows="6"
							required
							value={userInfos.message}
							onChange={e => handleInputChange('message', e.target.value, e.target)}
							onFocus={e => handleInputFocus(e)}
							onBlur={e => handleInputBlur(e)}
						/>
					</div>
					<Button
						className="Contact__FormSubmit"
						label="Submit"
						isFilled
						onClick={handleFormSubmit}
						colorText="#EAE7DD"
						colorBackground="#9E9A8E"
						colorBorder="none"
					/>
					<div className="Contact__FormMessages">
						<div className="Contact__FormMessage Contact__FormMessage--Success capsSmall">{successMessage}</div>
						<div className="Contact__FormMessage Contact__FormMessage--Error capsSmall">{errorMessage}</div>
						{isFormLoading &&
							<div className="Contact__FormMessage Contact__FormMessage--Loading capsSmall">
								Please wait while we are processing your request...
							</div>
						}
					</div>
				</form>
			</section>
			<section className="Contact__SecondSection" data-menu-color="lWhite,mWhite,sWhite">
				<p className="Contact__SecondSectionText h4">
					{pageData.text.text}
				</p>
				<div className="Contact__SecondSectionButtons">
					<Button
						className="Contact__SecondSectionButton"
						label={pageData.first_button_text.text}
						link={pageData.first_button_link.url}
						target={pageData.first_button_link.target}
						colorText="#4C4B42"
						colorBackground="#EAE7DD"
						colorBorder="none"
					/>
					<Button
						className="Contact__SecondSectionButton"
						label={pageData.second_button_text.text}
						link={pageData.second_button_link.url}
						target={pageData.second_button_link.target}
						colorText="#EAE7DD"
						colorBackground="none"
						colorBorder="rgba(234, 231, 221, 0.2)"
					/>
				</div>
			</section>
    </main>
  )
}

export default Contact

export const query = graphql`
	query Contact {
		prismicContact {
			data {
				meta_description {
					text
				}
				catchphrase {
					text
				}
				text {
					text
				}
				first_button_link {
					url
					target
				}
				first_button_text {
					text
				}
				second_button_link {
					url
					target
				}
				second_button_text {
					text
				}
			}
		}
		prismicBannerAndPopup {
			...BannerAndPopup
			...CookiesNotice
		}
		prismicNewsletter {
			...Newsletter
		}
		prismicFooter {
			...Footer
		}
	}
`